import React from 'react';
import { Achievement, Chapter, Mission, Scenario, Skill } from '../../types';
import { getMissionStartScene } from '../../systems/mission-progress';
import { AssetField } from './AssetPickerModal';
import { uniqueId } from './utils';
import styles from './scenario-studio.module.css';

function SceneSelect({
  value,
  sceneIds,
  onChange,
  label = 'Scène de départ',
}: {
  value: string;
  sceneIds: string[];
  onChange: (v: string) => void;
  label?: string;
}) {
  return (
    <div className={styles.field}>
      <label>{label}</label>
      <select className={styles.select} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {sceneIds.map((id) => (
          <option key={id} value={id}>{id}</option>
        ))}
      </select>
    </div>
  );
}

interface MissionsEditorProps {
  scenarioId: string;
  missions: Mission[];
  chapters: Chapter[];
  scenes: Scenario['scenes'];
  onChange: (missions: Mission[]) => void;
}

export const MissionsEditor: React.FC<MissionsEditorProps> = ({
  scenarioId,
  missions,
  chapters,
  scenes,
  onChange,
}) => {
  const update = (index: number, patch: Partial<Mission>) => {
    onChange(missions.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...missions];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(newIdx, 0, item);
    onChange(next.map((m, i) => ({ ...m, order: i + 1 })));
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h4>Missions</h4>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() =>
            onChange([
              ...missions,
              {
                id: uniqueId('mission'),
                title: 'Nouvelle mission',
                order: missions.length + 1,
              },
            ])
          }
        >
          + Mission
        </button>
      </div>
      {missions.length === 0 && (
        <p className={styles.hint}>Optionnel : découpe le scénario en missions dans la bibliothèque.</p>
      )}
      {missions.map((mission, index) => (
        <div key={mission.id} className={styles.card}>
          <div className={styles.row}>
            <strong>Mission {index + 1}</strong>
            <div className={styles.inlineActions}>
              <button type="button" onClick={() => move(index, -1)} title="Monter">↑</button>
              <button type="button" onClick={() => move(index, 1)} title="Descendre">↓</button>
              <button type="button" onClick={() => onChange(missions.filter((_, i) => i !== index))} title="Supprimer">×</button>
            </div>
          </div>
          <div className={styles.field}>
            <label>ID</label>
            <input className={styles.input} value={mission.id} onChange={(e) => update(index, { id: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Titre</label>
            <input className={styles.input} value={mission.title} onChange={(e) => update(index, { title: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Scène de départ</label>
            <input
              className={styles.input}
              value={getMissionStartScene({ metadata: { missions, chapters }, scenes }, mission.id) ?? '—'}
              readOnly
              title="Dérivée du premier chapitre de la mission"
            />
          </div>
          <AssetField
            scenarioId={scenarioId}
            label="Miniature"
            value={mission.thumbnail ?? ''}
            subdir="backgrounds"
            onChange={(thumbnail) => update(index, { thumbnail: thumbnail || undefined })}
          />
          <div className={styles.row}>
            <div className={styles.field} style={{ flex: 1 }}>
              <label>Ordre</label>
              <input
                type="number"
                className={styles.input}
                value={mission.order}
                onChange={(e) => update(index, { order: parseInt(e.target.value, 10) || 1 })}
              />
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <label>Boucliers max (PEDA)</label>
              <input
                type="number"
                className={styles.input}
                value={mission.pedaShieldsMax ?? ''}
                onChange={(e) =>
                  update(index, { pedaShieldsMax: parseInt(e.target.value, 10) || undefined })
                }
              />
            </div>
          </div>
          <div className={styles.field}>
            <label>Mission requise (déblocage)</label>
            <select
              className={styles.select}
              value={mission.requiresMission ?? ''}
              onChange={(e) => update(index, { requiresMission: e.target.value || undefined })}
            >
              <option value="">— Aucune —</option>
              {missions
                .filter((m) => m.id !== mission.id)
                .map((m) => (
                  <option key={m.id} value={m.id}>{m.title} ({m.id})</option>
                ))}
            </select>
          </div>
          <button type="button" className={styles.btnDanger} onClick={() => onChange(missions.filter((_, i) => i !== index))}>
            Supprimer
          </button>
        </div>
      ))}
    </div>
  );
};

interface ChaptersEditorProps {
  scenarioId: string;
  chapters: Chapter[];
  sceneIds: string[];
  missionIds: string[];
  onChange: (chapters: Chapter[]) => void;
}

export const ChaptersEditor: React.FC<ChaptersEditorProps> = ({
  scenarioId,
  chapters,
  sceneIds,
  missionIds,
  onChange,
}) => {
  const update = (index: number, patch: Partial<Chapter>) => {
    onChange(chapters.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...chapters];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(newIdx, 0, item);
    onChange(next);
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h4>Chapitres</h4>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() =>
            onChange([
              ...chapters,
              {
                id: uniqueId('chapitre'),
                title: 'Nouveau chapitre',
                startScene: sceneIds[0] ?? '',
              },
            ])
          }
        >
          + Chapitre
        </button>
      </div>
      {chapters.map((chapter, index) => (
        <div key={chapter.id} className={styles.card}>
          <div className={styles.row}>
            <strong>Chapitre {index + 1}</strong>
            <div className={styles.inlineActions}>
              <button type="button" onClick={() => move(index, -1)} title="Monter">↑</button>
              <button type="button" onClick={() => move(index, 1)} title="Descendre">↓</button>
              <button type="button" onClick={() => onChange(chapters.filter((_, i) => i !== index))} title="Supprimer">×</button>
            </div>
          </div>
          <div className={styles.field}>
            <label>ID</label>
            <input className={styles.input} value={chapter.id} onChange={(e) => update(index, { id: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Titre</label>
            <input className={styles.input} value={chapter.title} onChange={(e) => update(index, { title: e.target.value })} />
          </div>
          <SceneSelect
            value={chapter.startScene}
            sceneIds={sceneIds}
            onChange={(startScene) => update(index, { startScene })}
          />
          <AssetField
            scenarioId={scenarioId}
            label="Miniature (optionnel)"
            value={chapter.thumbnail ?? ''}
            subdir="backgrounds"
            onChange={(thumbnail) => update(index, { thumbnail: thumbnail || undefined })}
          />
          {missionIds.length > 0 && (
            <div className={styles.field}>
              <label>Mission associée</label>
              <select
                className={styles.select}
                value={chapter.missionId ?? ''}
                onChange={(e) => update(index, { missionId: e.target.value || undefined })}
              >
                <option value="">— Toutes —</option>
                {missionIds.map((id) => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </select>
            </div>
          )}
          <button type="button" className={styles.btnDanger} onClick={() => onChange(chapters.filter((_, i) => i !== index))}>
            Supprimer
          </button>
        </div>
      ))}
    </div>
  );
};

interface AchievementsEditorProps {
  scenarioId: string;
  achievements: Achievement[];
  onChange: (achievements: Achievement[]) => void;
}

export const AchievementsEditor: React.FC<AchievementsEditorProps> = ({
  scenarioId,
  achievements,
  onChange,
}) => {
  const update = (index: number, patch: Partial<Achievement>) => {
    onChange(achievements.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h4>Achievements</h4>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() =>
            onChange([
              ...achievements,
              {
                id: uniqueId('achievement'),
                name: 'Nouveau succès',
                description: '',
                condition: 'flag:',
              },
            ])
          }
        >
          + Achievement
        </button>
      </div>
      {achievements.map((achievement, index) => (
        <div key={achievement.id} className={styles.card}>
          <div className={styles.field}>
            <label>ID</label>
            <input className={styles.input} value={achievement.id} onChange={(e) => update(index, { id: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Nom</label>
            <input className={styles.input} value={achievement.name} onChange={(e) => update(index, { name: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Description</label>
            <textarea
              className={styles.textarea}
              rows={2}
              value={achievement.description}
              onChange={(e) => update(index, { description: e.target.value })}
            />
          </div>
          <div className={styles.field}>
            <label>Condition (ex. flag:hasKey)</label>
            <input
              className={styles.input}
              value={achievement.condition}
              onChange={(e) => update(index, { condition: e.target.value })}
              placeholder="flag:nom_du_flag"
            />
          </div>
          <AssetField
            scenarioId={scenarioId}
            label="Icône (optionnel)"
            value={achievement.icon ?? ''}
            subdir="backgrounds"
            onChange={(icon) => update(index, { icon: icon || undefined })}
          />
          <button type="button" className={styles.btnDanger} onClick={() => onChange(achievements.filter((_, i) => i !== index))}>
            Supprimer
          </button>
        </div>
      ))}
    </div>
  );
};

interface SkillsEditorProps {
  scenarioId: string;
  skills: Skill[];
  onChange: (skills: Skill[]) => void;
}

export const SkillsEditor: React.FC<SkillsEditorProps> = ({
  scenarioId,
  skills,
  onChange,
}) => {
  const update = (index: number, patch: Partial<Skill>) => {
    onChange(skills.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h4>Skills</h4>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() =>
            onChange([
              ...skills,
              {
                id: uniqueId('skill'),
                name: 'Nouvelle compétence',
                description: '',
              },
            ])
          }
        >
          + Skill
        </button>
      </div>
      {skills.map((skill, index) => (
        <div key={skill.id} className={styles.card}>
          <div className={styles.field}>
            <label>ID</label>
            <input className={styles.input} value={skill.id} onChange={(e) => update(index, { id: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Nom</label>
            <input className={styles.input} value={skill.name} onChange={(e) => update(index, { name: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Description</label>
            <textarea
              className={styles.textarea}
              rows={2}
              value={skill.description}
              onChange={(e) => update(index, { description: e.target.value })}
            />
          </div>
          <AssetField
            scenarioId={scenarioId}
            label="Icône (optionnel)"
            value={skill.icon ?? ''}
            subdir="backgrounds"
            onChange={(icon) => update(index, { icon: icon || undefined })}
          />
          <button type="button" className={styles.btnDanger} onClick={() => onChange(skills.filter((_, i) => i !== index))}>
            Supprimer
          </button>
        </div>
      ))}
    </div>
  );
};
